<template>
    <div class="header-landing-page-container">
        <header class="header-landing-page" :style="style">
            <div class="header-landing-page__inner">
                <div class="header-landing-page__top">
                    <component
                        :is="visualHeadings ? 'span' : 'h1'"
                        v-if="title"
                        class="header-landing-page__heading u-heading-1"
                    >
                        {{ decodeHtmlEntities(title) }}
                    </component>

                    <component
                        :is="visualHeadings ? 'span' : 'h2'"
                        v-if="subtitle"
                        class="header-landing-page__subheading u-heading-4"
                    >
                        {{ decodeHtmlEntities(subtitle) }}
                    </component>
                </div>
                <div
                    v-if="ctaBlock"
                    class="header-landing-page__content"
                >
                    <div class="header-landing-page__left">
                        <slot name="visual"></slot>

                        <form
                            v-if="ctaBlock"
                            class="header-landing-page__form"
                            @submit.prevent="submitForm"
                        >
                            <div
                                v-if="ctaBlockOverwrite.heading || ctaBlock.heading"
                                class="header-landing-page__cta-title u-heading-4"
                            >
                                {{ ctaBlockOverwrite.heading || ctaBlock.heading }}
                            </div>

                            <div
                                v-if="ctaBlockOverwrite.subheading || ctaBlock.subheading"
                                class="header-landing-page__cta-text"
                            >
                                {{ ctaBlockOverwrite.subheading || ctaBlock.subheading }}
                            </div>

                            <DsAtomInput
                                v-if="ctaBlock.input.show"
                                id="input"
                                name="input"
                                type="text"
                                class="header-landing-page__input"
                                :label="ctaBlock.input.label"
                                :placeholder="ctaBlock.input.placeholder"
                                :mask="ctaBlock.input.mask"
                                :validation="ctaBlock.input.validation"
                                :value="inputValue"
                                :is-valid="valid"
                                size="big"
                                :error="ctaBlock.input.errorMessage"
                                :icon="ctaBlock.input.icon"
                                :prefix="ctaBlock.input.prefix"
                                :suffix="ctaBlock.input.suffix"
                                @update-value="updateValue"
                                @update-validation="updateValidation"
                            />

                            <div class="header-landing-page__button-container">
                                <DsAtomButton
                                    class="header-landing-page__button"
                                    :text="ctaBlockOverwrite.buttonLabel || ctaBlock.button.text"
                                    :icon-right="ctaBlock?.button?.icon"
                                    :full-width="true"
                                    :enable-underline="true"
                                    :underline-text="ctaBlock.tinyText"
                                    underline-alignment="center"
                                >
                                    <template #underlineText>
                                        <span class="u-tiny-text u-bold">
                                            {{ ctaBlock.tinyText }}
                                        </span>
                                    </template>
                                </DsAtomButton>
                            </div>
                        </form>
                    </div>

                    <div
                        v-if="usps"
                        class="header-landing-page__usps"
                    >
                        <WatsonOrganismContentColumnCheckmarks
                            :content="{}"
                            :items="usps.items"
                            column-count="1"
                            :turn-on-background="false"
                            :background="null"
                            :icon-color="usps.iconColor"
                            :text-color="usps.textColor"
                            :typography="usps.typography"
                            media-position="next-to-text"
                        />
                    </div>
                </div>
            </div>
        </header>
    </div>
</template>

<script setup>
import { computed, ref } from 'vue';

import { validateValue } from '@leads.io/design-system-component-library';
import { DsAtomButton, DsAtomInput } from '@leads.io/design-system-component-library/marketplaces';

import WatsonOrganismContentColumnCheckmarks from '@organisms/content-column-checkmarks/watson-organism-content-column-checkmarks.vue';
import { handleClickAction } from '@utils/click-action';
import decodeHtmlEntities from '../../../js/utils/decode-html-entities';

const props = defineProps({
    title: {
        type: String,
        required: false,
        default: null,
    },
    subtitle: {
        type: String,
        required: false,
        default: '',
    },
    visualHeadings: {
        type: Boolean,
        required: false,
        default: false,
    },
    ctaBlock: {
        type: Object,
        required: false,
        default: null,
    },
    usps: {
        type: Object,
        required: false,
        default: null,
    },
    ctaBlockOverwrite: {
        type: Object,
        required: false,
        default: () => ({}),
    },
    background: {
        type: String,
        required: false,
        default: null,
    },
    textColor: {
        type: String,
        required: false,
        default: null,
    },
});

const inputValue = ref('');
const valid = ref(true);

const submitForm = () => {
    if (props.ctaBlock.input.show) {
        if (!validateValue(inputValue.value, props.ctaBlock.input.validation)) {
            valid.value = false;
            return;
        }
    }

    handleClickAction(props.ctaBlock.clickAction, inputValue.value);
};

const updateValue = value => {
    inputValue.value = value;
};

const updateValidation = value => {
    valid.value = value;
};

const style = computed(() => ({
    '--background': props.background || 'initial',
    '--text-color': props.textColor || 'initial',
}));
</script>

<style lang="scss">
@use 'watson-organism-header-landing-page';
</style>
